import c from 'classnames';
import styled, { css } from 'styled-components';
import { Dayjs } from '@lgg/isomorphic/dayjs';
import { Icon } from 'src/components/general/icon';
import { LggDatePicker } from 'src/components/general/inputs/date-picker';
import { StyledInputLabel } from 'src/components/general/inputs/generic-input';
import { FlexColumn } from 'src/components/layout/flex-column';
import { useFormatDate } from 'src/hooks/use-format-date';
import { inputFocusBorder } from 'src/theme/sub-themes/inputs-theme';

const CalendarInputSuffixIcon = styled(Icon)`
  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const CalendarInputClearIcon = styled(Icon)`
  align-items: center;
  background: ${({ theme }) => theme.colors.koala};
  border-radius: 50%;
  display: flex;
  height: 14px;
  justify-content: center;
  margin: 2px;
  width: 14px;

  svg {
    height: 6px;
    width: 6px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const StyledDatePicker = styled(LggDatePicker)<{ $isActive: boolean }>`
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  height: 38px;
  line-height: 16px;
  padding: 11px 15px 11px 10px;

  ${({ $isActive }) =>
    $isActive &&
    css`
      ${inputFocusBorder};
    `};
`;

const LggDatePickerInputWrapper = styled(FlexColumn)`
  .ant-dropdown.context-menu {
    min-width: max-content !important;
  }
`;

type LggDatePickerInputProps = {
  value: Dayjs | null;
  placeholderText?: string;
  onFocus?: VoidFunction;
  isActive?: boolean;
  hasError?: boolean;
  onChange?: (date: Dayjs | null) => void;
  disabled?: boolean;
  label?: string;
  onClick?: VoidFunction;
  formatHandler?: (date: Dayjs) => string;
  showSuffixIcon?: boolean;
  ['data-lgg-id']?: string;
  allowClear?: boolean;
};

export const LggDatePickerInput = ({
  value,
  isActive = false,
  placeholderText,
  onFocus,
  disabled = false,
  onChange,
  label,
  onClick,
  formatHandler,
  hasError,
  showSuffixIcon = true,
  allowClear = true,
  ...rest
}: LggDatePickerInputProps) => {
  const { formatDateAndTimePickerDate } = useFormatDate();

  return (
    <LggDatePickerInputWrapper {...rest} className="lgg-date-time-picker-input">
      {label && <StyledInputLabel>{label}</StyledInputLabel>}
      <StyledDatePicker
        allowClear={allowClear}
        clearIcon={
          showSuffixIcon ? (
            <CalendarInputClearIcon type="closeBold" data-lgg-id="input-clear-icon" />
          ) : (
            <></>
          )
        }
        inputReadOnly={true}
        open={false}
        panelRender={() => null}
        disabled={disabled}
        suffixIcon={
          showSuffixIcon ? (
            <CalendarInputSuffixIcon
              type="calendarNoPadding"
              lggTestId="input-calendar-icon"
            />
          ) : (
            <></>
          )
        }
        format={formatHandler ?? ((date) => formatDateAndTimePickerDate(date.toDate()))}
        value={value}
        placeholder={placeholderText}
        onFocus={onFocus}
        onChange={(date) => {
          onChange?.(date);
        }}
        data-lgg-id={`${rest['data-lgg-id']}-input`}
        $isActive={isActive}
        onClick={onClick}
        className={c({ 'input-error': hasError })}
      />
    </LggDatePickerInputWrapper>
  );
};
